import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { Menu, X, Phone, Mail, MapPin, UserRound } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useConfig } from "../../lib/config";

const LINKS = [["/", "home"], ["/about", "about"], ["/services", "services"], ["/pricing", "pricing"], ["/faq", "faq"], ["/contact", "contact"]];

export const Logo = ({ light }) => (
  <Link to="/" data-testid="site-logo" className={`flex items-center ${light ? "bg-white rounded-xl px-2 py-1 w-fit" : ""}`}>
    <img src="/images/logo.png" alt="Mainliz Cleaning Services" className={light ? "h-12 w-auto" : "h-12 sm:h-14 w-auto"} />
  </Link>
);

export function LangToggle({ dark }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex rounded-full p-0.5 text-xs font-semibold ${dark ? "bg-slate-800" : "bg-slate-100"}`} data-testid="language-toggle">
      {["en", "es"].map((l) => (
        <button key={l} data-testid={`lang-${l}-button`} onClick={() => setLang(l)}
          className={`px-3 py-1 rounded-full transition-colors ${lang === l ? "bg-sky-600 text-white" : dark ? "text-slate-300" : "text-slate-600"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function AccountLinks({ onClick }) {
  const { t } = useLang();
  return (
    <>
      <Show when="signed-out">
        <Link to="/sign-in" onClick={onClick} className="text-sm font-medium text-slate-700 hover:text-sky-700" data-testid="header-sign-in-link">{t.nav.signIn}</Link>
        <Link to="/sign-up" onClick={onClick} className="text-sm font-medium text-sky-700 hover:text-sky-800" data-testid="header-sign-up-link">{t.nav.signUp}</Link>
      </Show>
      <Show when="signed-in">
        <Link to="/account" onClick={onClick} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-sky-700" data-testid="header-account-link"><UserRound size={16} />{t.nav.account}</Link>
        <UserButton />
      </Show>
    </>
  );
}

function Header() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const cls = ({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-sky-700" : "text-slate-600 hover:text-slate-900"}`;
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-sky-100">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3.5 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map(([to, k]) => <NavLink key={to} to={to} end className={cls} data-testid={`nav-${k}`}>{t.nav[k]}</NavLink>)}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <LangToggle />
          <AccountLinks />
          <Link to="/quote" className="btn-outline !py-2 text-sm" data-testid="header-quote-button">{t.nav.quote}</Link>
          <Link to="/book" className="btn-primary !py-2 text-sm" data-testid="header-book-button">{t.nav.book}</Link>
        </div>
        <div className="flex lg:hidden items-center gap-2">
          <LangToggle />
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-button" className="p-2 rounded-lg hover:bg-slate-100">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white px-4 py-4 space-y-3" data-testid="mobile-menu">
          {LINKS.map(([to, k]) => <NavLink key={to} to={to} end onClick={() => setOpen(false)} className="block py-1.5 text-slate-700 font-medium">{t.nav[k]}</NavLink>)}
          <div className="flex items-center gap-4 pt-2"><AccountLinks onClick={() => setOpen(false)} /></div>
          <div className="flex gap-2 pt-2">
            <Link to="/quote" onClick={() => setOpen(false)} className="btn-outline flex-1 text-sm">{t.nav.quote}</Link>
            <Link to="/book" onClick={() => setOpen(false)} className="btn-primary flex-1 text-sm">{t.nav.book}</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { t } = useLang();
  const cfg = useConfig();
  const b = cfg?.business || {};
  return (
    <footer className="bg-slate-900 text-slate-400">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2 space-y-4">
          <Logo light />
          <p className="max-w-sm text-sm">{t.footer.tag}</p>
          <div className="space-y-1.5 text-sm">
            <p className="flex items-center gap-2"><MapPin size={14} /> {b.address}</p>
            <p className="flex items-center gap-2"><Phone size={14} /> {b.phone}</p>
            <p className="flex items-center gap-2"><Mail size={14} /> {b.email}</p>
          </div>
        </div>
        <div className="space-y-2 text-sm">
          {LINKS.map(([to, k]) => <Link key={to} to={to} className="block hover:text-white">{t.nav[k]}</Link>)}
        </div>
        <div className="space-y-2 text-sm">
          <Link to="/book" className="block hover:text-white">{t.nav.book}</Link>
          <Link to="/quote" className="block hover:text-white">{t.nav.quote}</Link>
          <Link to="/my-bookings" className="block hover:text-white" data-testid="footer-my-bookings">{t.nav.myBookings}</Link>
          <Link to="/admin" className="block hover:text-white" data-testid="footer-staff-login">{t.nav.staff}</Link>
        </div>
      </div>
      <div className="border-t border-slate-800 py-5 text-center text-xs">© {new Date().getFullYear()} Mainliz Cleaning Services · House cleaning Hialeah · Cleaning services Miami-Dade · {t.footer.rights}</div>
    </footer>
  );
}

export default function SiteLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}

export const PageHead = ({ eyebrow, title, sub }) => (
  <section className="bg-gradient-to-b from-sky-50 to-white border-b border-sky-100">
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14 lg:py-20 fade-up">
      {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 max-w-3xl">{title}</h1>
      {sub && <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl">{sub}</p>}
    </div>
  </section>
);
